import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Star,
  MapPin,
  Thermometer,
  Users,
  Coffee,
  Waves,
} from "lucide-react";
import { hotSpringsData } from "../data/hotSprings";

const FeaturedSprings = () => {
  const [showAll, setShowAll] = useState(false);
  const [hideButton, setHideButton] = useState(false);
  const displayedSprings = showAll ? hotSpringsData : hotSpringsData.slice(0, 3);

  const handleExploreClick = () => {
    setShowAll(!showAll);
    setHideButton(true);
  };

  const getAmenityIcon = (amenity) => {
    const iconMap = {
      "Spa services": Waves,
      Restaurant: Coffee,
      Lodge: Users,
      "Full spa": Waves,
      "Fitness center": Users,
      Dining: Coffee,
      Accommodation: Users,
      "Changing rooms": Users,
      "Picnic areas": Coffee,
      "Pool complex": Waves,
      "Event space": Users,
    };

    const IconComponent = iconMap[amenity] || Users;
    return <IconComponent className="w-4 h-4" />;
  };

  return (
    <section id="springs" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
            DISCOVER. EXPLORE. RELAX.
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Mankato area is home to exceptional geothermal wellness destinations
            across the region. Your ultimate hot springs soaking experience
            awaits. Find a luxurious hot spring for your next wellness retreat.
          </p>
        </motion.div>

        <div className="space-y-16">
          {displayedSprings.map((spring, index) => (
            <motion.div
              key={spring.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                index % 2 === 1 ? "lg:grid-flow-col-dense" : ""
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Image */}
              <div className={`${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                  <motion.div
                    className="relative h-64 lg:h-80 rounded-2xl overflow-hidden shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Actual image with gradient overlay */}
                    <div className="absolute inset-0">
                      <img 
                        src={spring.image} 
                        alt={spring.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-spa-blue-400/20 to-spa-teal-400/20 flex items-center justify-center">
                        <div className="text-white/60 text-6xl font-serif">
                          {spring.name.charAt(0)}
                        </div>
                      </div>
                    </div>

                  {/* Overlay with spring type */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-spa-blue-700 text-xs font-medium rounded-full">
                      {spring.type}
                    </span>
                  </div>

                  {/* Rating overlay */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-amber-500 fill-current" />
                      <span className="text-sm font-medium text-gray-800">
                        {spring.rating}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div
                className={`${
                  index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""
                }`}
              >
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-serif font-bold text-gray-800 mb-2">
                      {spring.name}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{spring.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Thermometer className="w-4 h-4" />
                        <span>{spring.temperature}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 leading-relaxed">
                    {spring.description}
                  </p>

                  {/* Features */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Key Features
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {spring.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center space-x-2 text-sm text-gray-600"
                        >
                          <div className="w-1.5 h-1.5 bg-spa-blue-500 rounded-full"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Amenities */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Amenities
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {spring.amenities
                        .slice(0, 4)
                        .map((amenity, amenityIndex) => (
                          <div
                            key={amenityIndex}
                            className="flex items-center space-x-2 px-3 py-2 bg-spa-blue-50 text-spa-blue-700 rounded-lg text-sm"
                          >
                            {getAmenityIcon(amenity)}
                            <span>{amenity}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                 
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Explore All Button */}
        {!hideButton && (
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <motion.button
              className="button-primary text-lg px-8 py-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExploreClick}
            >
              EXPLORE ALL HOT SPRINGS
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FeaturedSprings;
